/**
 * Briques d'interface du menu : avatar et présence, étiquette de visibilité, fenêtre
 * modale, sélecteur segmenté, étoiles, et les formats (temps relatif, jour de jeu).
 */
import type { ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { Globe, Lock, Star, Users, X, type LucideIcon } from 'lucide-preact';
import { calendarDay, dayIndex, type Presence, type WorldVisibility } from '@snowchain/shared';

/* Formats ------------------------------------------------------------------ */

export function initials(name: string): string {
  return (
    name
      .split(/[\s'’-]+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  );
}

const TONES = ['var(--glacier-4)', 'var(--wood-3)', 'var(--pine-3)', 'var(--ember-4)', 'var(--night-3)', 'var(--glacier-3)', 'var(--wood-4)', 'var(--pine-4)'];

/** Couleur stable d'un joueur (dérivée de son identifiant). */
export function toneOf(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return TONES[Math.abs(h) % TONES.length] as string;
}

export function relTime(ts: number | null | undefined, now = Date.now()): string {
  if (!ts) return 'jamais';
  const s = Math.max(0, (now - ts) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  const d = Math.floor(s / 86400);
  if (d === 1) return 'hier';
  if (d < 30) return `il y a ${d} j`;
  return new Date(ts).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** « Jour 4 · mar. 15 déc. » pour une horloge de jeu. */
export function gameDate(gameTime: number): { day: number; label: string } {
  const day = dayIndex(gameTime);
  return { day, label: calendarDay(day).label };
}

export function presenceText(p: Presence): string {
  if (p.state === 'game') return p.world ? `En jeu · ${p.world.name}` : 'En partie';
  if (p.state === 'menu') return 'Au menu';
  return p.lastSeen ? `Hors ligne · ${relTime(p.lastSeen)}` : 'Hors ligne';
}

export const VISIBILITY: Record<WorldVisibility, { label: string; hint: string; icon: LucideIcon }> = {
  public: { label: 'Publique', hint: 'Visible par tous dans la liste des serveurs', icon: Globe },
  friends: { label: 'Amis', hint: 'Visible par vos amis seulement', icon: Users },
  private: { label: 'Privée', hint: 'Sur invitation ou avec le code de la partie', icon: Lock },
};

/** Lien qui ouvre directement la partie (après connexion). */
export function inviteLink(worldId: string): string {
  const url = new URL(location.href);
  url.search = '';
  url.hash = '';
  url.searchParams.set('world', worldId);
  return url.toString();
}

/** Code lu dans un lien d'invitation ou saisi tel quel. */
export function parseCode(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  try {
    if (/^https?:\/\//.test(s)) {
      const w = new URL(s).searchParams.get('world');
      if (w) return /^[A-Za-z0-9_-]{4,64}$/.test(w) ? w : null;
    }
  } catch {
    return null;
  }
  return /^[A-Za-z0-9_-]{4,64}$/.test(s) ? s : null;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Repli : sélection d'un champ temporaire (contexte non sécurisé, permission refusée).
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  }
}

/* Composants ----------------------------------------------------------------- */

export function Avatar(props: { id: string; name: string; size?: number; presence?: Presence['state'] | null; title?: string }) {
  const size = props.size ?? 36;
  return (
    <span class="mn-av" style={{ width: `${size}px`, height: `${size}px`, fontSize: `${Math.round(size * 0.38)}px`, background: toneOf(props.id) }} title={props.title ?? props.name} aria-hidden="true">
      {initials(props.name)}
      {props.presence && <span class={`mn-dot mn-dot-${props.presence}`} />}
    </span>
  );
}

/** Pile d'avatars (amis présents sur une partie). */
export function AvatarStack(props: { people: { id: string; name: string }[]; max?: number; size?: number }) {
  const max = props.max ?? 4;
  const shown = props.people.slice(0, max);
  const extra = props.people.length - shown.length;
  if (!props.people.length) return null;
  return (
    <span class="mn-stack" title={props.people.map((p) => p.name).join(', ')}>
      {shown.map((p) => (
        <Avatar key={p.id} id={p.id} name={p.name} size={props.size ?? 22} />
      ))}
      {extra > 0 && <span class="mn-stack-more">+{extra}</span>}
    </span>
  );
}

export function VisibilityTag(props: { v: WorldVisibility }) {
  const v = VISIBILITY[props.v];
  const Icon = v.icon;
  return (
    <span class={`mn-tag mn-tag-${props.v}`} title={v.hint}>
      <Icon size={12} />
      {v.label}
    </span>
  );
}

export function LiveTag(props: { live: boolean }) {
  return (
    <span class={`mn-live${props.live ? ' on' : ''}`} title={props.live ? 'Au moins un joueur : le temps avance' : 'Personne : le temps est arrêté jusqu’au retour d’un joueur'}>
      <span class="mn-live-dot" />
      {props.live ? 'En ligne' : 'En veille'}
    </span>
  );
}

export function Stars(props: { value: number; size?: number }) {
  const v = Math.max(0, Math.min(5, props.value));
  return (
    <span class="mn-stars" title={`Réputation ${Math.round((v / 5) * 100)} %`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} class="mn-star">
          <Star size={props.size ?? 12} />
          <span class="mn-star-fill" style={{ width: `${Math.max(0, Math.min(1, v - i)) * 100}%` }}>
            <Star size={props.size ?? 12} />
          </span>
        </span>
      ))}
    </span>
  );
}

export function Segmented<T extends string | number>(props: { value: T; options: { value: T; label: ComponentChildren; title?: string }[]; onChange: (v: T) => void; label: string; small?: boolean }) {
  return (
    <div class={`mn-seg${props.small ? ' mn-seg-sm' : ''}`} role="radiogroup" aria-label={props.label}>
      {props.options.map((o) => (
        <button key={String(o.value)} type="button" role="radio" aria-checked={o.value === props.value} class={o.value === props.value ? 'on' : ''} title={o.title} onClick={() => props.onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Field(props: { label: string; hint?: ComponentChildren; children: ComponentChildren; for?: string }) {
  return (
    <div class="mn-field">
      <label for={props.for}>{props.label}</label>
      {props.children}
      {props.hint && <div class="mn-hint">{props.hint}</div>}
    </div>
  );
}

export function Empty(props: { icon: LucideIcon; title: string; children?: ComponentChildren }) {
  const Icon = props.icon;
  return (
    <div class="mn-empty">
      <span class="mn-empty-ic">
        <Icon size={22} />
      </span>
      <p class="mn-empty-title">{props.title}</p>
      {props.children && <div class="mn-empty-body">{props.children}</div>}
    </div>
  );
}

export function Spinner() {
  return <span class="mn-spin" aria-label="Chargement" />;
}

/** Fenêtre modale : Échap et clic à côté ferment, le premier champ prend le focus. */
export function Dialog(props: { title: string; icon?: LucideIcon; onClose: () => void; children: ComponentChildren; footer?: ComponentChildren; wide?: boolean; tone?: 'danger' }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('input, select, textarea, button.mn-btn-primary');
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        props.onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      prev?.focus?.();
    };
  }, []);
  const Icon = props.icon;
  return (
    <div class="mn-modal" onMouseDown={(e) => e.target === e.currentTarget && props.onClose()}>
      <div class={`mn-dialog${props.wide ? ' mn-dialog-wide' : ''}${props.tone === 'danger' ? ' mn-dialog-danger' : ''}`} role="dialog" aria-modal="true" aria-label={props.title} ref={ref}>
        <div class="mn-dialog-head">
          {Icon && (
            <span class="mn-dialog-ic">
              <Icon size={18} />
            </span>
          )}
          <h2>{props.title}</h2>
          <button type="button" class="mn-ibtn" aria-label="Fermer" onClick={props.onClose}>
            <X size={16} />
          </button>
        </div>
        <div class="mn-dialog-body">{props.children}</div>
        {props.footer && <div class="mn-dialog-foot">{props.footer}</div>}
      </div>
    </div>
  );
}

export function Btn(props: {
  kind?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  onClick?: (e: MouseEvent) => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
  full?: boolean;
  children?: ComponentChildren;
  class?: string;
}) {
  const Icon = props.icon;
  const size = props.size ?? 'md';
  return (
    <button
      type={props.type ?? 'button'}
      class={`mn-btn mn-btn-${props.kind ?? 'secondary'} mn-btn-${size}${props.full ? ' mn-btn-full' : ''}${props.children ? '' : ' mn-btn-icon'} ${props.class ?? ''}`}
      onClick={props.onClick}
      disabled={props.disabled}
      title={props.title}
      aria-label={props.children ? undefined : props.title}
    >
      {Icon && <Icon size={size === 'sm' ? 14 : size === 'lg' ? 18 : 16} />}
      {props.children}
    </button>
  );
}
